/**
 * Componente de proteção de rota para página de Onboarding
 * Permite acesso apenas se:
 * - Usuário está logado E
 * - Usuário TEM PERMISSÃO para criar tenant (é root_admin ou não tem nenhuma role) E
 * - Usuário NÃO é operador White Label (defesa em profundidade — v1.1.0)
 *
 * CHANGELOG:
 *   v1.2.0 — Em caso de falha técnica no RPC `is_wl_user`, NÃO libera onboarding
 *            automaticamente (evita que operador WL crie tenant por engano).
 *            Mostra tela de erro com botão de retry.
 *   v1.1.0 — Bloqueia acesso de operadores White Label, redirecionando para /wl.
 */
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, AlertTriangle } from 'lucide-react';

interface OnboardingProtectedRouteProps {
  children: React.ReactNode;
}

type WlCheckState = 'idle' | 'checking' | 'not_wl' | 'is_wl' | 'error';

export const OnboardingProtectedRoute = ({ children }: OnboardingProtectedRouteProps) => {
  const { user, loading, dataLoaded, currentTenant, isTenantMember, roles, isRootAdmin } = useAuth();
  const [wlCheck, setWlCheck] = useState<WlCheckState>('idle');

  const hasAnyTenantRole = roles.some(r => r.tenant_id !== null);
  const canCreateTenant = isRootAdmin || (!hasAnyTenantRole && !isTenantMember);
  const wouldEnterOnboarding =
    !loading && dataLoaded && !!user && canCreateTenant && !currentTenant && !isTenantMember;

  const runWlCheck = () => {
    if (!user) return;
    setWlCheck('checking');
    supabase
      .rpc('is_wl_user', { _user_id: user.id })
      .then(({ data, error }) => {
        if (error) {
          // NÃO liberar onboarding em erro técnico — evita operador WL criar tenant indevido
          setWlCheck('error');
          return;
        }
        setWlCheck(data === true ? 'is_wl' : 'not_wl');
      });
  };

  useEffect(() => {
    if (!wouldEnterOnboarding || wlCheck !== 'idle' || !user) return;
    runWlCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wouldEnterOnboarding, wlCheck, user]);

  if (loading || !dataLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;
  if (!canCreateTenant) return <Navigate to="/tenant" replace />;
  if (currentTenant || isTenantMember) return <Navigate to="/tenant" replace />;

  if (wlCheck === 'checking' || wlCheck === 'idle') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (wlCheck === 'is_wl') return <Navigate to="/wl" replace />;

  if (wlCheck === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="max-w-md text-center space-y-4">
          <AlertTriangle className="h-10 w-10 text-destructive mx-auto" />
          <h1 className="text-xl font-semibold">Falha ao validar sessão</h1>
          <p className="text-sm text-muted-foreground">
            Não foi possível validar suas permissões. Tente novamente em alguns
            segundos antes de prosseguir.
          </p>
          <button
            onClick={() => { setWlCheck('idle'); }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default OnboardingProtectedRoute;

